import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Couriers() {
  const { couriers, loading, isAdmin, isAccount, isStore } = useWebData();

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [typeFilter, setTypeFilter] = useState('All'); 

  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!couriers) return;

    let result = [...couriers];

    // A. Type Filter
    if (typeFilter !== 'All') {
        result = result.filter(item => item.type === typeFilter);
    }

    // B. Search Filter
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(item => {
            const dateStr = formatDate(item.date).toLowerCase();
            const row = `${dateStr} ${item.courierName} ${item.docketNo} ${item.sender} ${item.receiver} ${item.status} ${item.contents} ${item.userName} ${item.type}`.toLowerCase();
            return row.includes(lower);
        });
    }

    // C. Date Filter
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.date || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    // Sort Newest First
    result.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    setFiltered(result);
  }, [couriers, searchTerm, viewMode, selectedDate, selectedYear, typeFilter]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD (Added Completion Date)
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Entry_Date: formatDate(i.date),
        Type: i.type,
        Docket_No: i.docketNo,
        Courier_Name: i.courierName,
        Sender: i.sender,
        Receiver: i.receiver,
        Contents: i.contents,
        Status: i.status,
        // 🔥 Shows Received Date for Inward, Delivery Date for Outward
        Actual_Received_Delivery_Date: i.completionDate ? formatDate(i.completionDate) : "-", 
        Notes: i.notes || "-",
        Added_By: i.userName
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Couriers");
    XLSX.writeFile(wb, `Couriers_Report_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  
  // 🔥 UPDATE STATUS & CAPTURE DATE
  const updateStatus = async (id, status) => {
      if(window.confirm(`Mark as ${status}?`)) {
          try {
              const now = new Date().toISOString(); // Capture Current Date
              
              await updateDoc(doc(db, "couriers", id), { 
                  status,
                  completionDate: now // 🔥 Saving the date when Marked Done
              });
              
              if(viewData && viewData.id === id) {
                  setViewData({...viewData, status, completionDate: now});
              }
          } catch(e) { alert("Error updating status"); }
      }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "couriers", editData.id), {
              courierName: editData.courierName,
              docketNo: editData.docketNo,
              status: editData.status,
              notes: editData.notes,
              completionDate: editData.completionDate // Editable Date
          });
          alert("Courier Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this record?")) await deleteDoc(doc(db, "couriers", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (dateStr) => {
      if(!dateStr) return "-";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-GB');
  };

  const canManage = isAdmin || isAccount || isStore;

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* --- HEADER --- */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#d35400", display:"flex", alignItems:"center"}}>
                📦 Couriers <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        {/* TABS */}
        <div style={{display:"flex", gap:"10px", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
            {['All', 'Inward', 'Outward'].map(type => (
                <button key={type} onClick={()=>setTypeFilter(type)} 
                    style={{
                        padding:"8px 20px", borderRadius:"20px", border:"none", cursor:"pointer", fontWeight:"bold", fontSize:"13px",
                        backgroundColor: typeFilter===type ? (type==='Inward'?'#27ae60':(type==='Outward'?'#2980b9':'#333')) : "#eee",
                        color: typeFilter===type ? "white" : "#555",
                        boxShadow: typeFilter===type ? "0 2px 5px rgba(0,0,0,0.2)" : "none",
                        transition: "0.2s"
                    }}>
                    {type === 'Inward' ? '📥 Inward' : (type === 'Outward' ? '📤 Outward' : '📦 All')}
                </button>
            ))}
        </div>

        {/* FILTERS */}
        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}>
                    {getYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Docket, Sender..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#d35400", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Docket No</th>
                    <th style={thStyle}>Courier</th>
                    <th style={thStyle}>Sender / Receiver</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No records found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(row.date)}</td>
                    <td style={tdStyle}>
                        <span style={{color: row.type==='Inward'?'green':'#2980b9', fontWeight:"bold", background: row.type==='Inward'?'#e8f5e9':'#e3f2fd', padding:"3px 8px", borderRadius:"10px", fontSize:"11px"}}>{row.type}</span>
                    </td>
                    <td style={{...tdStyle, fontWeight:"bold"}}>{row.docketNo}</td>
                    <td style={tdStyle}>{row.courierName}</td>
                    <td style={{...tdStyle, fontSize:"11px"}}>
                        <div>📤 <strong>From:</strong> {row.sender}</div>
                        <div>📥 <strong>To:</strong> {row.receiver}</div>
                    </td>
                    <td style={tdStyle}>
                        <span style={{
                            padding:"4px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:"bold",
                            backgroundColor: row.status==='Delivered'||row.status==='Received'?'#e8f5e9':'#fff3e0',
                            color: row.status==='Delivered'||row.status==='Received'?'green':'orange'
                        }}>
                            {row.status}
                        </span>
                    </td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            {canManage && row.status === 'Pending' && (
                                <button onClick={()=>updateStatus(row.id, row.type==='Inward'?'Received':'Delivered')} style={btnCheck} title="Mark Done">✔</button>
                            )}
                            <button onClick={() => setViewData(row)} style={btnIcon} title="View">👁️</button>
                            {canManage && (
                                <>
                                    <button onClick={() => setEditData(row)} style={btnIconEdit} title="Edit">✏️</button>
                                    <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                                </>
                            )}
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔥 VIEW MODAL (UPDATED with Dates) */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#d35400"}}>📦 Courier Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.8"}}>
                
                {/* DATES SECTION */}
                <div style={{display:"flex", justifyContent:"space-between", marginBottom:"15px", background:"#f5f5f5", padding:"10px", borderRadius:"6px"}}>
                    <div>
                        <p style={{margin:0, fontSize:"11px", color:"#555"}}>Entry Date</p>
                        <p style={{margin:0, fontWeight:"bold"}}>{formatDate(viewData.date)}</p>
                    </div>
                    
                    {/* 🔥 Shows Delivery/Received Date if available */}
                    {(viewData.status === 'Delivered' || viewData.status === 'Received') && (
                        <div style={{textAlign:"right"}}>
                            <p style={{margin:0, fontSize:"11px", color:"#555"}}>
                                {viewData.type === 'Inward' ? 'Received Date' : 'Delivery Date'}
                            </p>
                            <p style={{margin:0, fontWeight:"bold", color:"green"}}>
                                {viewData.completionDate ? formatDate(viewData.completionDate) : "Not Updated"}
                            </p>
                        </div>
                    )}
                </div>
                
                <p><strong>🚚 Courier:</strong> {viewData.courierName}</p>
                <p><strong>🔢 Docket:</strong> {viewData.docketNo}</p>
                <div style={{background:"#fff3e0", padding:"10px", borderRadius:"6px", margin:"10px 0", borderLeft:"4px solid #d35400"}}>
                    <p style={{margin:"0 0 5px 0"}}><strong>📤 Sender:</strong> {viewData.sender}</p>
                    <p style={{margin:0}}><strong>📥 Receiver:</strong> {viewData.receiver}</p>
                </div>
                <p><strong>📦 Contents:</strong> {viewData.contents}</p>
                <p><strong>📝 Notes:</strong> {viewData.notes || "-"}</p>
                <p><strong>👷 Added By:</strong> {viewData.userName}</p>
                
                {viewData.photoUrl && (
                    <div style={{marginTop:"15px", textAlign:"center"}}>
                        <a href={viewData.photoUrl} target="_blank" rel="noreferrer" style={btnLink}>View Attached Photo 📸</a>
                    </div>
                )}
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Courier</h3>
            <label style={labelStyle}>Courier Name</label>
            <input type="text" value={editData.courierName} onChange={(e)=>setEditData({...editData, courierName:e.target.value})} style={modalInput}/>
            
            <label style={labelStyle}>Docket No</label>
            <input type="text" value={editData.docketNo} onChange={(e)=>setEditData({...editData, docketNo:e.target.value})} style={modalInput}/>

            <div style={{display:"flex", gap:"10px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Status</label>
                    <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                        <option value="Pending">Pending</option>
                        <option value="In Transit">In Transit</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Received">Received</option>
                    </select>
                </div>
                {/* 🔥 Manual Date Edit */}
                <div style={{flex:1}}>
                    <label style={labelStyle}>Received/Delivered Date</label>
                    <input type="date" 
                        value={editData.completionDate ? editData.completionDate.split('T')[0] : ''} 
                        onChange={(e)=>setEditData({...editData, completionDate:e.target.value})} 
                        style={modalInput}
                    />
                </div>
            </div>

            <label style={labelStyle}>Notes</label>
            <input type="text" value={editData.notes || ''} onChange={(e)=>setEditData({...editData, notes:e.target.value})} style={modalInput}/>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};
const btnCheck={backgroundColor:"#27ae60", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#f39c12", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};
const btnLink={display:"block", padding:"10px", backgroundColor:"#34495e", color:"white", textDecoration:"none", borderRadius:"5px", fontWeight:"bold"};
const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#d35400", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default Couriers;